import React, { useEffect, useMemo, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Card, Col, ProgressBar, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import axiosInstance from "../services/axiosInterceptor";
import { useAuth } from "../services/authProvider";

const MONTH_NAMES = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

const BAR_OPTIONS = {
  chart: { type: 'bar', toolbar: { show: false }, stacked: false },
  colors: ['#198754', '#dc3545', '#fd7e14'],
  plotOptions: { bar: { columnWidth: '58%', borderRadius: 3 } },
  dataLabels: { enabled: false },
  legend: { position: 'top', fontSize: '12px' },
  xaxis: { categories: MONTH_NAMES, labels: { style: { fontSize: '11px' } } },
  yaxis: { labels: { style: { colors: '#6e7985', fontSize: '11px' } } },
  grid: { borderColor: 'rgba(72,94,144,0.07)', padding: { top: -5, bottom: 0 } },
  tooltip: { shared: true, intersect: false },
};

const QUICK_LINKS = [
  { icon: 'ri-calendar-check-line', label: 'เช็คชื่อเข้าเรียน', link: '/teaching/attendance', color: 'primary' },
  { icon: 'ri-bar-chart-box-line', label: 'รายงานการเข้าเรียน', link: '/academic/general-attendance-report', color: 'success' },
  { icon: 'ri-team-line', label: 'จัดการนักเรียน', link: '/registration/manage-classes', color: 'info' },
  { icon: 'ri-file-chart-line', label: 'ผลการเรียน', link: '/assessment/all-class-grade', color: 'warning' },
  { icon: 'ri-user-heart-line', label: 'นักเรียนในที่ปรึกษา', link: '/advisor/student-in-advice', color: 'danger' },
  { icon: 'ri-user-unfollow-line', label: 'แจ้งลานักเรียน', link: '/advisor/student-leave', color: 'warning' },
  { icon: 'ri-book-open-line', label: 'ห้องสมุด', link: '/library/home', color: 'secondary' },
  { icon: 'ri-shield-user-line', label: 'ติดตามนักเรียนขาดเรียน', link: '/discipline/absence-monitor', color: 'dark' },
  { icon: 'ri-bank-line', label: 'ธนาคารนักเรียน', link: '/bank/dashboard', color: 'info' },
  { icon: 'ri-home-heart-line', label: 'อนุญาตกลับบ้าน', link: '/discipline/home-leave', color: 'success' },
  { icon: 'ri-cellphone-line', label: 'แตะบัตรที่ประตู', link: '/guard/gate-scan', color: 'primary' },
  { icon: 'ri-door-open-line', label: 'จุดตรวจประตูโรงเรียน', link: '/guard/gate-pass', color: 'dark' },
  { icon: 'ri-calendar-close-line', label: 'แจ้งลานักเรียน', link: '/discipline/student-leave', color: 'warning' },
  { icon: 'ri-home-heart-line', label: 'ห้องเรียนของฉัน (ศาสนา)', link: '/islamic/advisor/home', color: 'success' },
  { icon: 'ri-checkbox-circle-line', label: 'เช็คชื่อศาสนา', link: '/islamic/my-subjects', color: 'primary' },
  { icon: 'ri-settings-3-line', label: 'จัดการห้องศาสนา', link: '/islamic/manage-rooms', color: 'info' },
  { icon: 'ri-grid-line', label: 'คะแนนภาคศาสนา', link: '/islamic/grades', color: 'warning' },
];

const QUICK_LINK_PERMISSIONS = {
  '/teaching/attendance': [1],
  '/academic/general-attendance-report': [3, 4, 99],
  '/registration/manage-classes': [8, 9, 99],
  '/assessment/all-class-grade': [10, 11, 99],
  '/advisor/student-in-advice': [2],
  '/advisor/student-leave': [2],
  '/library/home': [13, 99],
  '/discipline/absence-monitor': [16, 99],
  '/bank/dashboard': [18, 99],
  '/discipline/home-leave': [16, 99],
  '/guard/gate-scan': [16, 24, 99],
  '/guard/gate-pass': [16, 24, 99],
  '/discipline/student-leave': [16, 99],
  '/islamic/advisor/home': [51, 99],
  '/islamic/my-subjects': [50, 99],
  '/islamic/manage-rooms': [52, 99],
  '/islamic/grades': [50, 99],
};

// ช่องยอด ขาด/สาย/ลา กดเข้าไปดูรายชื่อได้ ส่วน "มาเรียน" ไม่มี status จึงเป็นกล่องเฉยๆ
function StatusTile({ item, to }) {
  const inner = (
    <>
      <h5 className={`mb-0 text-${item.color} fw-bold`}>{item.count.toLocaleString()}</h5>
      <span className="fs-xs text-secondary">{item.label}</span>
    </>
  );
  if (!item.status) {
    return <div className={`p-2 rounded bg-${item.color} bg-opacity-10`}>{inner}</div>;
  }
  return (
    <Link
      to={`${to}?status=${item.status}`}
      className={`d-block p-2 rounded bg-${item.color} bg-opacity-10 text-decoration-none status-tile-link`}
      title={`ดูรายชื่อนักเรียนที่${item.label}วันนี้`}
    >
      {inner}
      <i className={`ri-arrow-right-s-line fs-xs text-${item.color} d-block`}></i>
    </Link>
  );
}

function formatDateTH(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('th-TH', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

export default function SchoolStatistic() {
  const { posIds } = useAuth();
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const [trendTab, setTrendTab] = useState('general');

  const allowedQuickLinks = QUICK_LINKS.filter(link => {
    const required = QUICK_LINK_PERMISSIONS[link.link] || [];
    return required.some(pos => posIds.includes(pos));
  });

  useEffect(() => {
    if (skin === 'dark') {
      document.querySelectorAll('.btn-white').forEach(btn => {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      });
    } else {
      document.querySelectorAll('.btn-outline-primary').forEach(btn => {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      });
    }
  }, [skin]);

  useEffect(() => {
    axiosInstance.get('/api/getDashboardData')
      .then(res => setData(res.data))
      .catch(err => {
        console.error('Dashboard fetch error:', err);
        setFetchError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const year        = data?.year?.year_year ?? '-';
  const summary     = data?.summary ?? {};
  const att         = data?.todayAttendance ?? {};
  const monthlyTrend = data?.monthlyTrend ?? [];
  const roomCheck        = data?.roomCheck        ?? { total: 0, checked: 0 };
  const islamicRoomCheck = data?.islamicRoomCheck ?? { total: 0, checked: 0 };
  const today            = data?.today ?? '';

  const islamicSummary = data?.islamicSummary ?? {};
  const islamicAtt     = data?.islamicTodayAttendance ?? {};
  const islamicStudents = Number(islamicSummary.students?.total) || 0;
  const islamicMale     = Number(islamicSummary.students?.male)  || 0;
  const islamicFemale   = Number(islamicSummary.students?.female)|| 0;
  const islamicRooms    = Number(islamicSummary.rooms?.total)    || 0;
  const islamicPresent  = Number(islamicAtt.present_count) || 0;
  const islamicAbsent   = Number(islamicAtt.absent_count)  || 0;
  const islamicLate     = Number(islamicAtt.late_count)    || 0;
  const islamicLeave    = Number(islamicAtt.leave_count)   || 0;
  const islamicTotal    = islamicPresent + islamicAbsent + islamicLate + islamicLeave;
  const islamicRate     = islamicTotal > 0 ? Math.round((islamicPresent / islamicTotal) * 100) : null;
  const showIslamicStats = posIds.some(p => [50, 51, 52, 53, 99].includes(p));

  const totalStudents  = Number(summary.students?.total)  || 0;
  const maleStudents   = Number(summary.students?.male)   || 0;
  const femaleStudents = Number(summary.students?.female) || 0;
  const totalTeachers  = Number(summary.teachers?.total)  || 0;
  const totalRooms     = Number(summary.rooms?.total)     || 0;

  const presentCount = Number(att.present_count) || 0;
  const absentCount  = Number(att.absent_count)  || 0;
  const lateCount    = Number(att.late_count)    || 0;
  const leaveCount   = Number(att.leave_count)   || 0;
  const totalChecked = presentCount + absentCount + lateCount + leaveCount;
  const attendanceRate = totalChecked > 0 ? Math.round((presentCount / totalChecked) * 100) : 0;

  const roomCheckPercent = roomCheck.total > 0
    ? Math.round((roomCheck.checked / roomCheck.total) * 100)
    : 0;

  const islamicRoomCheckPercent = islamicRoomCheck.total > 0
    ? Math.round((islamicRoomCheck.checked / islamicRoomCheck.total) * 100)
    : 0;

  const dorm         = data?.dormitory ?? {};
  const dormOverview = dorm.overview ?? {
    total_villages: 0, total_buildings: 0, total_capacity: 0, current_occupancy: 0,
    available_spaces: 0, occupancy_rate: 0, full_buildings: 0, available_buildings: 0,
  };
  const dormMale     = dorm.by_gender?.male   ?? { total_capacity: 0, current_occupancy: 0, available_spaces: 0, occupancy_rate: 0, building_count: 0 };
  const dormFemale   = dorm.by_gender?.female ?? { total_capacity: 0, current_occupancy: 0, available_spaces: 0, occupancy_rate: 0, building_count: 0 };

  const monthlyMap  = {};
  monthlyTrend.forEach(m => { monthlyMap[m.att_month] = m; });
  const presentData = Array.from({ length: 12 }, (_, i) => Number(monthlyMap[i + 1]?.present) || 0);
  const absentData  = Array.from({ length: 12 }, (_, i) => Number(monthlyMap[i + 1]?.absent)  || 0);
  const lateData    = Array.from({ length: 12 }, (_, i) => Number(monthlyMap[i + 1]?.late)    || 0);

  const barSeries = [
    { name: 'มาเรียน',  data: presentData },
    { name: 'ขาดเรียน', data: absentData },
    { name: 'สาย',      data: lateData },
  ];

  const islamicMonthlyTrend = data?.islamicMonthlyTrend ?? [];
  const islamicMonthlyMap   = {};
  islamicMonthlyTrend.forEach(m => { islamicMonthlyMap[m.att_month] = m; });
  const islamicPresentData = Array.from({ length: 12 }, (_, i) => Number(islamicMonthlyMap[i + 1]?.present) || 0);
  const islamicAbsentData  = Array.from({ length: 12 }, (_, i) => Number(islamicMonthlyMap[i + 1]?.absent)  || 0);
  const islamicLateData    = Array.from({ length: 12 }, (_, i) => Number(islamicMonthlyMap[i + 1]?.late)    || 0);

  const islamicBarSeries = [
    { name: 'มาเรียน',  data: islamicPresentData },
    { name: 'ขาดเรียน', data: islamicAbsentData },
    { name: 'สาย',      data: islamicLateData },
  ];

  const donutOptions = useMemo(() => ({
    chart: { type: 'donut' },
    colors: ['#198754', '#dc3545', '#fd7e14', '#0d6efd'],
    labels: ['มาเรียน', 'ขาด', 'สาย', 'ลา'],
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'เช็คชื่อแล้ว',
              fontSize: '11px',
              formatter: () => totalChecked.toLocaleString() + ' ครั้ง',
            },
          },
        },
      },
    },
    stroke: { width: 2 },
  }), [totalChecked]);

  const islamicDonutOptions = useMemo(() => ({
    chart: { type: 'donut' },
    colors: ['#198754', '#dc3545', '#fd7e14', '#0d6efd'],
    labels: ['มาเรียน', 'ขาด', 'สาย', 'ลา'],
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'เช็คชื่อแล้ว',
              fontSize: '11px',
              formatter: () => islamicTotal.toLocaleString() + ' ครั้ง',
            },
          },
        },
      },
    },
    stroke: { width: 2 },
  }), [islamicTotal]);

  const summaryCards = [
    {
      icon: 'ri-group-fill',
      bgColor: 'primary',
      value: totalStudents.toLocaleString(),
      label: 'นักเรียนทั้งหมด',
      sub: (
        <div className="d-flex gap-3 fs-xs text-secondary mt-1">
          <span><i className="ri-men-line text-info me-1"></i>ชาย {maleStudents.toLocaleString()}</span>
          <span><i className="ri-women-line text-danger me-1"></i>หญิง {femaleStudents.toLocaleString()}</span>
        </div>
      ),
    },
    {
      icon: 'ri-user-star-fill',
      bgColor: 'success',
      value: totalTeachers.toLocaleString(),
      label: 'ครู / บุคลากร',
      sub: <span className="fs-xs text-secondary">ทั้งหมดในระบบ</span>,
    },
    {
      icon: 'ri-door-open-fill',
      bgColor: 'warning',
      value: totalRooms,
      label: 'ห้องเรียน',
      sub: <span className="fs-xs text-secondary">ปีการศึกษา {year}</span>,
    },
    {
      icon: 'ri-calendar-check-fill',
      bgColor: attendanceRate >= 80 ? 'success' : 'danger',
      value: `${attendanceRate}%`,
      label: 'การเข้าเรียนวันนี้',
      sub: <span className="fs-xs text-secondary">รวม {totalChecked.toLocaleString()} ครั้งที่เช็คชื่อ</span>,
    },
  ];

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">หน้าหลัก</Link></li>
              <li className="breadcrumb-item active">สถิติภาพรวมโรงเรียน</li>
            </ol>
            <h4 className="main-title mb-0">ภาพรวมโรงเรียน</h4>
            <p className="text-secondary fs-sm mb-0">ปีการศึกษา {year} &nbsp;·&nbsp; {formatDateTH(today)}</p>
          </div>
        </div>

        {fetchError && (
          <div className="alert alert-warning d-flex align-items-center gap-2 mb-3">
            <i className="ri-error-warning-line fs-18"></i>
            ไม่สามารถโหลดข้อมูลได้ กรุณารีเฟรชหน้าหรือติดต่อผู้ดูแลระบบ
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">กำลังโหลด...</span>
            </div>
          </div>
        ) : (
          <Row className="g-3">

            {/* Quick Links */}
            {allowedQuickLinks.length > 0 && (
              <Col xs="12">
                <Card className="card-one">
                  <Card.Header>
                    <Card.Title as="h6" className="mb-0">เมนูด่วน</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3">
                    <Row className="g-2">
                      {allowedQuickLinks.map((item, i) => (
                        <Col xs="6" sm="4" md="3" xl="2" key={i}>
                          <Link to={item.link} className="text-decoration-none">
                            <div
                              className={`d-flex align-items-center gap-2 p-2 rounded border border-${item.color} border-opacity-25 bg-${item.color} bg-opacity-10`}
                              style={{ minHeight: 52 }}
                            >
                              <i className={`${item.icon} text-${item.color} fs-20 flex-shrink-0`}></i>
                              <span className="fs-sm fw-medium text-dark">{item.label}</span>
                            </div>
                          </Link>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            )}

            {/* Summary Cards */}
            {summaryCards.map((card, idx) => (
              <Col xs="6" xl="3" key={idx}>
                <Card className="card-one h-100">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-start gap-3">
                      <div
                        className={`flex-shrink-0 bg-${card.bgColor} bg-opacity-15 text-${card.bgColor} rounded-3 d-flex align-items-center justify-content-center`}
                        style={{ width: 52, height: 52 }}
                      >
                        <i className={`${card.icon} fs-22`}></i>
                      </div>
                      <div className="flex-grow-1 min-w-0">
                        <h2 className="card-value mb-0 fs-26">{card.value}</h2>
                        <label className="card-label fw-medium text-dark d-block">{card.label}</label>
                        {card.sub}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}

            {/* Islamic Stats */}
            {showIslamicStats && islamicStudents > 0 && (
              <Col xs="12">
                <Card className="card-one border-0" style={{ background: 'linear-gradient(135deg, rgba(13,110,253,0.06) 0%, rgba(25,135,84,0.06) 100%)' }}>
                  <Card.Header className="bg-transparent border-bottom-0 pb-0">
                    <Card.Title as="h6" className="mb-0 d-flex align-items-center gap-2">
                      <i className="ri-moon-fill text-primary"></i>
                      ภาคศาสนา — ปีการศึกษา {year}
                    </Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3 pt-2">
                    <Row className="g-2">
                      <Col xs="6" sm="3">
                        <div className="d-flex align-items-center gap-2 p-2 rounded bg-white bg-opacity-75">
                          <div className="flex-shrink-0 bg-primary bg-opacity-15 text-primary rounded-3 d-flex align-items-center justify-content-center" style={{ width: 42, height: 42 }}>
                            <i className="ri-group-fill fs-18"></i>
                          </div>
                          <div>
                            <div className="fw-bold fs-18 lh-1">{islamicStudents.toLocaleString()}</div>
                            <div className="fs-xs text-secondary">นักเรียน</div>
                            <div className="fs-xs text-secondary">
                              <i className="ri-men-line text-info me-1"></i>{islamicMale}
                              <i className="ri-women-line text-danger ms-2 me-1"></i>{islamicFemale}
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs="6" sm="3">
                        <div className="d-flex align-items-center gap-2 p-2 rounded bg-white bg-opacity-75">
                          <div className="flex-shrink-0 bg-warning bg-opacity-15 text-warning rounded-3 d-flex align-items-center justify-content-center" style={{ width: 42, height: 42 }}>
                            <i className="ri-door-open-fill fs-18"></i>
                          </div>
                          <div>
                            <div className="fw-bold fs-18 lh-1">{islamicRooms}</div>
                            <div className="fs-xs text-secondary">ห้องเรียน</div>
                          </div>
                        </div>
                      </Col>
                      <Col xs="6" sm="3">
                        <div className="d-flex align-items-center gap-2 p-2 rounded bg-white bg-opacity-75">
                          <div className={`flex-shrink-0 bg-${islamicRate === null ? 'secondary' : islamicRate >= 80 ? 'success' : 'danger'} bg-opacity-15 text-${islamicRate === null ? 'secondary' : islamicRate >= 80 ? 'success' : 'danger'} rounded-3 d-flex align-items-center justify-content-center`} style={{ width: 42, height: 42 }}>
                            <i className="ri-calendar-check-fill fs-18"></i>
                          </div>
                          <div>
                            <div className="fw-bold fs-18 lh-1">{islamicRate !== null ? `${islamicRate}%` : '-'}</div>
                            <div className="fs-xs text-secondary">เข้าเรียนวันนี้</div>
                            {islamicTotal > 0 && <div className="fs-xs text-secondary">{islamicTotal.toLocaleString()} ครั้ง</div>}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            )}

            {/* Monthly Attendance Bar Chart — tabbed สามัญ / ศาสนา */}
            <Col xl="12">
              <Card className="card-one">
                <Card.Header className="d-flex align-items-center flex-wrap gap-2">
                  <Card.Title as="h6" className="mb-0">แนวโน้มการเข้าเรียนรายเดือน</Card.Title>
                  <Badge bg="secondary" className="ms-auto fs-xs fw-normal">ปีการศึกษา {year}</Badge>
                  {showIslamicStats && (
                    <div className="d-flex gap-1">
                      <button
                        className={`btn btn-sm py-0 px-2 ${trendTab === 'general' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setTrendTab('general')}
                      >สามัญ</button>
                      <button
                        className={`btn btn-sm py-0 px-2 ${trendTab === 'islamic' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setTrendTab('islamic')}
                      >ศาสนา</button>
                    </div>
                  )}
                </Card.Header>
                <Card.Body className="p-3">
                  <ReactApexChart
                    series={trendTab === 'islamic' ? islamicBarSeries : barSeries}
                    options={BAR_OPTIONS}
                    type="bar"
                    height={260}
                  />
                </Card.Body>
              </Card>
            </Col>

            {/* Today's Attendance — split สามัญ / ศาสนา */}
            <Col xl={showIslamicStats ? 6 : 12}>
              <Card className="card-one h-100">
                <Card.Header className="d-flex align-items-center">
                  <Card.Title as="h6" className="mb-0">
                    การเข้าเรียนวันนี้{showIslamicStats ? ' — ภาคสามัญ' : ''}
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-3 d-flex flex-column">
                  {totalChecked > 0 ? (
                    <>
                      <ReactApexChart
                        series={[presentCount, absentCount, lateCount, leaveCount]}
                        options={donutOptions}
                        type="donut"
                        height={200}
                      />
                      <div className="d-flex flex-column gap-2 mt-3">
                        {[
                          { label: 'มาเรียน',  count: presentCount, color: '#198754' },
                          { label: 'ขาดเรียน', count: absentCount,  color: '#dc3545' },
                          { label: 'สาย',      count: lateCount,    color: '#fd7e14' },
                          { label: 'ลา',       count: leaveCount,   color: '#0d6efd' },
                        ].map((item, i) => (
                          <div key={i} className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-2">
                              <span className="d-inline-block rounded-circle flex-shrink-0" style={{ width: 10, height: 10, background: item.color }} />
                              <span className="fs-sm text-secondary">{item.label}</span>
                            </div>
                            <span className="fw-semibold text-dark">{item.count.toLocaleString()} ครั้ง</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-secondary py-5 flex-grow-1 d-flex flex-column align-items-center justify-content-center">
                      <i className="ri-inbox-2-line fs-40 mb-2 opacity-25 d-block"></i>
                      <p className="fs-sm mb-0">ยังไม่มีข้อมูลการเช็คชื่อวันนี้</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {showIslamicStats && (
              <Col xl="6">
                <Card className="card-one h-100">
                  <Card.Header className="d-flex align-items-center">
                    <Card.Title as="h6" className="mb-0">การเข้าเรียนวันนี้ — ภาคศาสนา</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3 d-flex flex-column">
                    {islamicTotal > 0 ? (
                      <>
                        <ReactApexChart
                          series={[islamicPresent, islamicAbsent, islamicLate, islamicLeave]}
                          options={islamicDonutOptions}
                          type="donut"
                          height={200}
                        />
                        <div className="d-flex flex-column gap-2 mt-3">
                          {[
                            { label: 'มาเรียน',  count: islamicPresent, color: '#198754' },
                            { label: 'ขาดเรียน', count: islamicAbsent,  color: '#dc3545' },
                            { label: 'สาย',      count: islamicLate,    color: '#fd7e14' },
                            { label: 'ลา',       count: islamicLeave,   color: '#0d6efd' },
                          ].map((item, i) => (
                            <div key={i} className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center gap-2">
                                <span className="d-inline-block rounded-circle flex-shrink-0" style={{ width: 10, height: 10, background: item.color }} />
                                <span className="fs-sm text-secondary">{item.label}</span>
                              </div>
                              <span className="fw-semibold text-dark">{item.count.toLocaleString()} ครั้ง</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center text-secondary py-5 flex-grow-1 d-flex flex-column align-items-center justify-content-center">
                        <i className="ri-inbox-2-line fs-40 mb-2 opacity-25 d-block"></i>
                        <p className="fs-sm mb-0">ยังไม่มีข้อมูลการเช็คชื่อวันนี้</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            )}

            {/* Room Check Progress — สามัญ */}
            <Col xl={showIslamicStats ? 6 : 12}>
              <Card className="card-one">
                <Card.Header>
                  <Card.Title as="h6" className="mb-0">
                    สถานะการเช็คชื่อวันนี้{showIslamicStats ? ' — ภาคสามัญ' : ''}
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="fs-sm text-secondary">ห้องเรียนที่เช็คชื่อแล้ว</span>
                    <span className="fw-semibold">
                      {roomCheck.checked} <span className="text-secondary fw-normal">/ {roomCheck.total} ห้อง</span>
                    </span>
                  </div>
                  <ProgressBar
                    now={roomCheckPercent}
                    variant={roomCheckPercent >= 100 ? 'success' : roomCheckPercent >= 50 ? 'warning' : 'danger'}
                    style={{ height: 10, borderRadius: 6 }}
                  />
                  <small className="text-secondary mt-1 d-block">
                    {roomCheckPercent}% ของทั้งหมด {roomCheck.total} ห้อง
                  </small>
                  <hr className="my-3" />
                  <Row className="g-2 text-center">
                    {[
                      { label: 'มาเรียน',  count: presentCount, color: 'success' },
                      { label: 'ขาดเรียน', count: absentCount,  color: 'danger',  status: 'absent' },
                      { label: 'สาย',      count: lateCount,    color: 'warning', status: 'late' },
                      { label: 'ลา',       count: leaveCount,   color: 'primary', status: 'leave' },
                    ].map((item, i) => (
                      <Col key={i} xs="3">
                        <StatusTile item={item} to="/dashboard/today-attendance" />
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* Room Check Progress — ศาสนา */}
            {showIslamicStats && (
              <Col xl="6">
                <Card className="card-one">
                  <Card.Header>
                    <Card.Title as="h6" className="mb-0">สถานะการเช็คชื่อวันนี้ — ภาคศาสนา</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fs-sm text-secondary">ห้องเรียนที่เช็คชื่อแล้ว</span>
                      <span className="fw-semibold">
                        {islamicRoomCheck.checked} <span className="text-secondary fw-normal">/ {islamicRoomCheck.total} ห้อง</span>
                      </span>
                    </div>
                    <ProgressBar
                      now={islamicRoomCheckPercent}
                      variant={islamicRoomCheckPercent >= 100 ? 'success' : islamicRoomCheckPercent >= 50 ? 'warning' : 'danger'}
                      style={{ height: 10, borderRadius: 6 }}
                    />
                    <small className="text-secondary mt-1 d-block">
                      {islamicRoomCheckPercent}% ของทั้งหมด {islamicRoomCheck.total} ห้อง
                    </small>
                    <hr className="my-3" />
                    <Row className="g-2 text-center">
                      {[
                        { label: 'มาเรียน',  count: islamicPresent, color: 'success' },
                        { label: 'ขาดเรียน', count: islamicAbsent,  color: 'danger',  status: 'absent' },
                        { label: 'สาย',      count: islamicLate,    color: 'warning', status: 'late' },
                        { label: 'ลา',       count: islamicLeave,   color: 'primary', status: 'leave' },
                      ].map((item, i) => (
                        <Col key={i} xs="3">
                          <StatusTile item={item} to="/dashboard/today-attendance-islamic" />
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            )}

            {/* ===== DORMITORY SECTION ===== */}
            <Col xl="12">
              <div className="d-flex align-items-center gap-3 mt-2">
                <h5 className="mb-0 fw-semibold text-dark d-flex align-items-center gap-2">
                  <i className="ri-home-8-fill text-primary"></i>หอพักนักเรียน
                </h5>
                <hr className="flex-grow-1" />
              </div>
            </Col>

            {/* Dormitory Overview */}
            <Col xs="12" xl="4">
              <Card className="card-one h-100">
                <Card.Header>
                  <Card.Title as="h6" className="mb-0">ภาพรวมหอพัก</Card.Title>
                  <Link to="/dormitory/home" className="ms-auto fs-xs text-primary">ดูรายละเอียด</Link>
                </Card.Header>
                <Card.Body className="p-3">
                  <div className="text-center mb-3">
                    <h2 className="fw-bold text-dark mb-0">
                      {Number(dormOverview.current_occupancy).toLocaleString()}
                      <span className="fs-16 fw-normal text-secondary"> / {Number(dormOverview.total_capacity).toLocaleString()}</span>
                    </h2>
                    <p className="text-secondary fs-sm mb-2">นักเรียนพักอาศัย / ความจุทั้งหมด</p>
                    <ProgressBar
                      now={dormOverview.occupancy_rate}
                      variant={dormOverview.occupancy_rate >= 90 ? 'danger' : dormOverview.occupancy_rate >= 70 ? 'warning' : 'success'}
                      style={{ height: 10, borderRadius: 6 }}
                    />
                    <small className="text-secondary">{dormOverview.occupancy_rate}% อัตราการใช้งาน</small>
                  </div>
                  <Row className="g-2 text-center mt-2">
                    {[
                      { label: 'หมู่บ้าน',        value: dormOverview.total_villages,   color: 'primary' },
                      { label: 'อาคาร',            value: dormOverview.total_buildings,  color: 'info' },
                      { label: 'ที่ว่างเหลือ',     value: dormOverview.available_spaces, color: 'success' },
                      { label: 'อาคารที่เต็มแล้ว', value: dormOverview.full_buildings,   color: 'danger' },
                    ].map((item, i) => (
                      <Col xs="6" key={i}>
                        <div className={`p-2 rounded bg-${item.color} bg-opacity-10`}>
                          <h5 className={`mb-0 text-${item.color} fw-bold`}>{Number(item.value).toLocaleString()}</h5>
                          <span className="fs-xs text-secondary">{item.label}</span>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* Male / Female Dorm Cards */}
            {[
              { label: 'หอพักชาย', icon: 'ri-men-line',   color: 'info',   data: dormMale },
              { label: 'หอพักหญิง', icon: 'ri-women-line', color: 'danger', data: dormFemale },
            ].map((g, i) => (
              <Col xs="6" xl="4" key={i}>
                <Card className="card-one h-100">
                  <Card.Header>
                    <i className={`${g.icon} text-${g.color} me-2`}></i>
                    <Card.Title as="h6" className="mb-0">{g.label}</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3 d-flex flex-column justify-content-between">
                    <div className="text-center mb-3">
                      <h2 className="fw-bold text-dark mb-0">
                        {Number(g.data.current_occupancy).toLocaleString()}
                        <span className="fs-16 fw-normal text-secondary"> / {Number(g.data.total_capacity).toLocaleString()}</span>
                      </h2>
                      <p className="text-secondary fs-sm mb-0">คน / ความจุ</p>
                    </div>
                    <div>
                      <div className="d-flex justify-content-between fs-xs text-secondary mb-1">
                        <span>อัตราการใช้งาน</span>
                        <span className="fw-semibold">{g.data.occupancy_rate}%</span>
                      </div>
                      <ProgressBar
                        now={g.data.occupancy_rate}
                        variant={g.data.occupancy_rate >= 90 ? 'danger' : g.data.occupancy_rate >= 70 ? 'warning' : g.color}
                        style={{ height: 8, borderRadius: 6 }}
                        className="mb-3"
                      />
                      <Row className="g-1 text-center">
                        <Col xs="6">
                          <div className={`p-2 rounded bg-${g.color} bg-opacity-10`}>
                            <div className={`fs-sm fw-bold text-${g.color}`}>{Number(g.data.building_count).toLocaleString()}</div>
                            <div className="fs-xs text-secondary">อาคาร</div>
                          </div>
                        </Col>
                        <Col xs="6">
                          <div className="p-2 rounded bg-success bg-opacity-10">
                            <div className="fs-sm fw-bold text-success">{Number(g.data.available_spaces).toLocaleString()}</div>
                            <div className="fs-xs text-secondary">ที่ว่างเหลือ</div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}

          </Row>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
